import { SingleDropdown, type SingleDropdownOption } from "@/components/molecules/SingleDropdown/SingleDropdown";
import { weatherParameterFieldDisplayName } from "@/i18n/weather-parameter-fields";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import { useEffect } from "react";
import { useController } from "react-hook-form";
import { useValueNameCreator } from "../../SmartFormUtils/FormHooks/useFormInstructions";
import type { InputInstruction } from "../../SmartFormUtils/commonTypeUtils";

import { FormField } from "@/overlay/components/form-field";
import styles from "./style.module.scss";

type InputProps = {
  instruction: InputInstruction<HasLayerIdxNarrowing<PartialWeatherParameter>>;
  parameters: Array<HasLayerIdxNarrowing<PartialWeatherParameter>>;
};

export function ToggleParametersGroup({ parameters, instruction }: InputProps) {
  const { createInputName } = useValueNameCreator(instruction);
  const { formMethods, currValue } = instruction;

  const {
    field: { onChange, value },
  } = useController({
    name: createInputName(),
    control: formMethods.control,
    defaultValue: currValue,
  });

  useEffect(() => {
    formMethods.setValue(createInputName(), currValue);
  }, [createInputName, currValue, formMethods]);

  const selectedParameterName = value?.parameter.name ?? currValue.parameter.name;

  if (parameters.length > 2) {
    const options: SingleDropdownOption[] = parameters.map((param) => ({
      value: param.parameter.name,
      label: param.parameter.description_en,
    }));

    return (
      <FormField label={weatherParameterFieldDisplayName.type()}>
        <SingleDropdown
          options={options}
          value={selectedParameterName}
          onChange={(newValue) => {
            const selectedParameter = parameters.find((param) => param.parameter.name === newValue);
            if (selectedParameter) {
              onChange(selectedParameter);
            }
          }}
        />
      </FormField>
    );
  }

  return (
    <div className={styles["toggleable-group"]}>
      {parameters.map((param) => (
        <div key={param.parameter.name} className={styles.toggleable}>
          <input
            type="radio"
            id={param.parameter.name}
            value={param.parameter.name}
            checked={selectedParameterName === param.parameter.name}
            onChange={() => {
              onChange(param);
            }}
          />
          <label htmlFor={param.parameter.name} className={styles.toggle}>
            <span>{param.parameter.description_en}</span>
          </label>
        </div>
      ))}
    </div>
  );
}
